import React, { useState } from 'react'
import { COLORS, EmptyState, INPUT_STYLE, Kpi, ProgressBar, addDays, fmtShort, pct, todayIso } from '../FieldOpsKit'
import { isDelayed } from '../store'

/**
 * Delivery report: on-time delivery, failed attempts and why, and the result per driver and per day for the last
 * 7 or 14 days. On time = delivered on or before the date it was originally planned for.
 */
export default function ReportTab({ ctx }) {
    const drv = ctx.state.drivers
    const today = todayIso()
    const [range, setRange] = useState(7)
    const from = addDays(today, -(range - 1))
    const list = drv.deliveries.filter((d) => d.originalDate >= from && d.originalDate <= today)

    const delivered = list.filter((d) => d.status === 'delivered')
    const onTime = delivered.filter((d) => d.deliveredOn <= d.originalDate)
    const failedEvents = []
    list.forEach((d) => d.events.forEach((e) => { if (e.type === 'failed') failedEvents.push({ d: d, reason: e.note.split(' · ')[0].split(' → ')[0] }) }))
    const cancelled = list.filter((d) => d.status === 'cancelled')
    const open = list.filter(isDelayed)

    const reasons = {}
    failedEvents.forEach((f) => { reasons[f.reason] = (reasons[f.reason] || 0) + 1 })
    const reasonList = Object.keys(reasons).map((r) => ({ r: r, n: reasons[r] })).sort((a, b) => b.n - a.n)

    const perDriver = drv.drivers.map((x) => {
        const mine = list.filter((d) => d.driverId === x.id && d.status !== 'cancelled')
        const done = mine.filter((d) => d.status === 'delivered')
        return { x: x, total: mine.length, done: done.length, onTime: done.filter((d) => d.deliveredOn <= d.originalDate).length, failed: failedEvents.filter((f) => f.d.driverId === x.id).length, weight: done.reduce((a, d) => a + d.weightLb, 0) }
    }).filter((r) => r.total > 0)

    const days = []
    for (let i = range - 1; i >= 0; i--) {
        const date = addDays(today, -i)
        const dl = list.filter((d) => d.originalDate === date && d.status !== 'cancelled')
        days.push({ date: date, total: dl.length, done: dl.filter((d) => d.status === 'delivered').length })
    }
    const maxDay = Math.max(1, ...days.map((d) => d.total))

    return (
        <React.Fragment>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                <select value={range} onChange={(e) => setRange(Number(e.target.value))} style={INPUT_STYLE} aria-label="Range">
                    <option value={7}>Last 7 days</option><option value={14}>Last 14 days</option>
                </select>
                <span style={{ fontSize: '0.78rem', color: COLORS.slate }}>{fmtShort(from)} – {fmtShort(today)} · based on the planned date of each delivery</span>
            </div>
            {list.length === 0 ? <EmptyState title="No deliveries in this range" /> : (
                <React.Fragment>
                    <div className="row g-3 mb-3">
                        <div className="col-6 col-lg-3"><Kpi label="Delivered" value={delivered.length} sub={'of ' + (list.length - cancelled.length) + ' planned'} status="success" /></div>
                        <div className="col-6 col-lg-3"><Kpi label="On time" value={pct(onTime.length, delivered.length) + '%'} sub={(delivered.length - onTime.length) + ' delivered late'} status={pct(onTime.length, delivered.length) >= 85 ? 'success' : 'warning'} /></div>
                        <div className="col-6 col-lg-3"><Kpi label="Failed attempts" value={failedEvents.length} sub={cancelled.length + ' cancelled'} status={failedEvents.length > 0 ? 'danger' : 'success'} /></div>
                        <div className="col-6 col-lg-3"><Kpi label="Still open" value={open.length} sub="delayed, never closed" status={open.length > 0 ? 'danger' : 'success'} /></div>
                    </div>
                    <div className="row g-4">
                        <div className="col-lg-7">
                            <h6 className="card-title-clean mb-2">By driver</h6>
                            <div className="tableFixHeadSmall mb-4"><table className="table table-clean">
                                <thead><tr><th>Driver</th><th>Assigned</th><th>Delivered</th><th>On time</th><th>Failed</th><th>Weight</th></tr></thead>
                                <tbody>{perDriver.map((r) => (
                                    <tr key={r.x.id}><td style={{ fontWeight: 700 }}>{r.x.name}</td><td>{r.total}</td><td style={{ minWidth: 130 }}><ProgressBar value={r.done} max={r.total} /></td><td>{pct(r.onTime, r.done)}%</td><td>{r.failed}</td><td>{r.weight.toLocaleString('en-US')} lb</td></tr>
                                ))}</tbody>
                            </table></div>
                            <h6 className="card-title-clean mb-2">By day</h6>
                            <div className="card"><div className="card-body" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 150, padding: '16px 16px 8px' }}>
                                {days.map((d) => (
                                    <div key={d.date} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                                        <div style={{ fontSize: '0.7rem', color: COLORS.slate }}>{d.done}/{d.total}</div>
                                        <div style={{ height: 80, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                            <div style={{ width: '70%', height: (d.total / maxDay * 100) + '%', background: COLORS.soft, borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: pct(d.done, d.total) + '%', background: COLORS.green }}></div>
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '0.68rem', color: COLORS.muted, marginTop: 4 }}>{fmtShort(d.date)}</div>
                                    </div>
                                ))}
                            </div></div>
                        </div>
                        <div className="col-lg-5">
                            <div className="card"><div className="card-body">
                                <h6 className="card-title-clean mb-2">Why deliveries failed</h6>
                                {reasonList.length === 0 ? <div style={{ color: COLORS.muted, fontSize: '0.85rem' }}>No failed attempts in this range.</div> : reasonList.map((x) => (
                                    <div key={x.r} style={{ marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}><span>{x.r}</span><strong>{x.n}</strong></div>
                                        <ProgressBar value={x.n} max={reasonList[0].n} height={6} color={COLORS.purple} hideLabel />
                                    </div>
                                ))}
                            </div></div>
                        </div>
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    )
}
